import { Order, NextPaymentResponse } from '../models/APIModels';
import { axiosKK } from '../services/networkRequest';

/**
 * ORDER STATUS COMPUTATION DOCUMENTATION
 * 
 * This file contains utilities for computing order statuses across different order types.
 * Each order type uses a different status system:
 * 
 * **STORAGE ORDERS** (storageRequired = true):
 *   - Uses `order.statuses[]` array with StatusResponse objects
 *   - Each status has a `statusId` (1-8) that maps to Hubspot stages
 *   - Statuses are updated via Hubspot webhook integration or admin panel
 *   - Status progression: Order Placed (1) → Paid in Full (2) → Boxes Sent (3) → 
 *     Boxes Received (4) → Boxes Requested (5) → Order Complete (6) → 
 *     Monthly Payments Started (7) → Final Bill Paid (8)
 *   - Most status updates are automated based on payment/webhook events
 *   - Statuses 5, 6, 7, 8 can be manually updated by admins
 * 
 * **SHIPPING ORDERS** (storageRequired = false, suitcaseOrder = false):
 *   - Uses same `order.statuses[]` array system as Storage orders
 *   - Simpler progression: Order Placed (1) → Paid in Full (2) → Boxes Sent (3) → 
 *     Boxes Received (4) → Order Complete (6)
 *   - Skips storage-related statuses (no monthly payments)
 *   - Status updates follow same Hubspot webhook/admin panel flow
 * 
 * **REMOVAL ORDERS** (orderType === 'removal'):
 *   - Uses `order.status` string field (NOT the statuses array)
 *   - Simple string values: 'pending', 'confirmed', 'cancelled'
 *   - Status is set automatically at payment confirmation ('pending' → 'confirmed')
 *   - Cancellation is manual via admin panel ('confirmed' → 'cancelled')
 *   - This is a separate system from Storage/Shipping orders
 * 
 * ============================================================================
 * STATUS MAPPING TABLES (Backend/HubSpot → Customer Display)
 * ============================================================================
 * 
 * **STORAGE ORDERS STATUS MAPPING:**
 * 
 * | Backend statusId | Customer Sees | Notes |
 * |------------------|---------------|-------|
 * | 1                | "Order Placed" | Initial order creation |
 * | 2                | "Paid in Full" | Payment confirmed |
 * | 3                | "Boxes Sent" | Boxes dispatched to customer |
 * | 4                | "Boxes Received" | Customer received boxes |
 * | 5                | "Boxes Requested" | Customer requested return |
 * | 6                | "Order Complete" | Order fulfillment complete |
 * | 7                | "Monthly Payments Started" | Recurring billing active |
 * | 8                | "Final Bill Paid" | All charges settled |
 * | **FALLBACK 1**   | **"Processing"** | `order.statuses` is null/undefined/empty array |
 * | **FALLBACK 2**   | **"Processing"** | No valid statusIds extracted (all null/undefined/invalid) |
 * | **FALLBACK 3**   | **"Processing"** | statusId outside 1-8 range or not in StorageStatusMap |
 * 
 * **SHIPPING ORDERS STATUS MAPPING:**
 * 
 * | Backend statusId | Customer Sees | Notes |
 * |------------------|---------------|-------|
 * | 1                | "Processing" | Order Placed (shown as Processing for shipping) |
 * | 2                | "Paid" | Payment confirmed |
 * | 3                | "Shipped" | Boxes dispatched |
 * | 4                | "Received" | Customer received boxes |
 * | 5                | "Received" | Same as 4 (simplified mapping) |
 * | 6                | "Delivered" | Order fulfillment complete |
 * | 7                | "Delivered" | Same as 6 (simplified mapping) |
 * | 8                | "Delivered" | Same as 6 (simplified mapping) |
 * | **FALLBACK 1**   | **"Processing"** | `order.statuses` is null/undefined/empty array |
 * | **FALLBACK 2**   | **"Processing"** | No valid statusIds extracted (all null/undefined/invalid) |
 * | **FALLBACK 3**   | **"Processing"** | statusId is 1 (Order Placed) - shown as Processing |
 * 
 * **REMOVAL ORDERS STATUS MAPPING:**
 * 
 * | Backend order.status | Customer Sees | Notes |
 * |----------------------|--------------|-------|
 * | "pending"           | "Pending" | Order created, awaiting confirmation |
 * | "confirmed"         | "Confirmed" | Payment confirmed, move scheduled |
 * | "cancelled"         | "Cancelled" | Order cancelled by admin/customer |
 * | **FALLBACK 1**      | **"Pending"** | `order.status` is null/undefined (defaults to 'pending') |
 * | **FALLBACK 2**      | **Capitalized status** | Unknown status value (e.g., "in_progress" → "In_progress") |
 * 
 * ============================================================================
 * FALLBACK LOGIC DETAILS
 * ============================================================================
 * 
 * **Storage & Shipping Orders:**
 *   - Fallback 1: Triggered when `order.statuses` is falsy, undefined, null, or empty array []
 *   - Fallback 2: Triggered when all statusIds are invalid (null, undefined, not a number, or outside 1-8 range)
 *   - Fallback 3: Triggered when statusId lookup fails (shouldn't happen with improved filter, but safety check)
 *   - All fallbacks return "Processing" to indicate order is being processed
 * 
 * **Removal Orders:**
 *   - Fallback 1: When `order.status` is missing/null/undefined, defaults to 'pending' → "Pending"
 *   - Fallback 2: When status value doesn't match known values, returns capitalized version of raw string
 *   - Uses `order.status` field directly (ignores `order.statuses[]` array completely)
 * 
 * **Data Validation:**
 *   - statusId values are validated to be: typeof 'number', not null, not undefined, and between 1-8
 *   - Invalid values are filtered out before processing
 *   - Math.max() is used to find the highest (most advanced) statusId when multiple exist
 */

export const StorageStatusMap: { [key: number]: string } = {
  1: 'Order Placed',
  2: 'Paid in Full',
  3: 'Boxes Sent',
  4: 'Boxes Received',
  5: 'Boxes Requested',
  6: 'Order Complete',
  7: 'Monthly Payments Started',
  8: 'Final Bill Paid',
};

export const getItemsInStorage = (order: Order): number => {
  return order.orderBoxes?.length || 0;
};

export const getOrderTypeLabel = (order: Order): string => {
  if (order.orderType === 'removal') {
    return 'Removal';
  }
  if (order.suitcaseOrder) {
    return 'Suitcase';
  }
  if (order.storageRequired) {
    return 'Storage';
  }
  return 'Shipping';
};

export function getStorageOrderStatus(order: Order): string {
  // Fallback 1: No statuses array or empty array
  if (!order.statuses || order.statuses.length === 0) {
    return 'Processing';
  }
  
  // Extract valid statusIds (1-8, excluding null/undefined/invalid)
  const statusIds = order.statuses
    .map(s => s.statusId)
    .filter(id => id !== undefined && id !== null && typeof id === 'number' && id >= 1 && id <= 8);
  
  // Fallback 2: No valid statusIds extracted
  if (statusIds.length === 0) {
    return 'Processing';
  }
  
  const highestStatusId = Math.max(...statusIds);
  
  // Fallback 3: statusId not in StorageStatusMap (shouldn't happen with filter, but safety check)
  return StorageStatusMap[highestStatusId] || 'Processing';
}

export function getShippingOrderStatus(order: Order): string {
  // Fallback 1: No statuses array or empty array
  if (!order.statuses || order.statuses.length === 0) {
    return 'Processing';
  }
  
  // Extract valid statusIds (1-8, excluding null/undefined/invalid)
  const statusIds = order.statuses
    .map(s => s.statusId)
    .filter(id => id !== undefined && id !== null && typeof id === 'number' && id >= 1 && id <= 8);
  
  // Fallback 2: No valid statusIds extracted
  if (statusIds.length === 0) {
    return 'Processing';
  }
  
  const highestStatusId = Math.max(...statusIds);
  
  // Simplified mapping for shipping orders
  if (highestStatusId >= 6) {
    return 'Delivered';
  } else if (highestStatusId >= 4) {
    return 'Received';
  } else if (highestStatusId >= 3) {
    return 'Shipped';
  } else if (highestStatusId >= 2) {
    return 'Paid';
  }
  
  // Fallback 3: statusId is 1 (Order Placed) - show as Processing for shipping
  return 'Processing';
}

export function getRemovalOrderStatus(order: Order): string {
  // Fallback 1: No status field - default to 'pending'
  const status = order.status || 'pending';
  const statusMap: { [key: string]: string } = {
    'pending': 'Pending',
    'confirmed': 'Confirmed',
    'cancelled': 'Cancelled',
  };
  
  // Fallback 2: Unknown status value - return capitalized version of raw status
  return statusMap[status.toLowerCase()] || status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

export function getOrderStatus(order: Order): string {
  if (order.orderType === 'removal') {
    return getRemovalOrderStatus(order);
  }
  
  if (order.storageRequired) {
    return getStorageOrderStatus(order);
  }
  
  return getShippingOrderStatus(order);
}

export const fetchNextPayment = async (orderId: number): Promise<NextPaymentResponse | null> => {
  try {
    const response = await axiosKK.get<NextPaymentResponse>(`/order/${orderId}/next-payment`);
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    console.error(`Error fetching next payment for order ${orderId}:`, error);
    return null;
  }
};

export const isOrderCompleted = (order: Order): boolean => {
  // Check for soft-deleted orders (deletedAt field indicates soft deletion)
  if (order.deletedAt && typeof order.deletedAt === 'number' && order.deletedAt > 0) {
    return true;
  }

  if (order.orderType === 'removal') {
    return order.status === 'cancelled' || order.status === 'completed';
  }

  if (!order.statuses || order.statuses.length === 0) {
    return false;
  }

  const statusIds = order.statuses
    .map(s => s.statusId)
    .filter(id => id !== undefined && id !== null && typeof id === 'number' && id >= 1 && id <= 8);

  if (statusIds.length === 0) {
    return false;
  }

  const highestStatusId = Math.max(...statusIds);

  if (order.storageRequired) {
    return highestStatusId === 6 || highestStatusId === 8;
  }

  return highestStatusId >= 6;
};

